import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { renderAcademicMarkdown } from '../lib/markdown';
import DownloadButton from '../components/DownloadButton';
import ApaReference from '../components/ApaReference';
import { safeText } from '../lib/render';

export default function AssignmentView() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.get(`/api/assignments/${id}`);
        setData(res);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'Failed to load assignment');
      }
    })();
  }, [id]);

  const rendered = useMemo(() => {
    if (!data) return { html: '', references: [] };
    const raw = data.content || '';
    const isHtml = raw.includes('<p>') || raw.includes('<h') || raw.includes('<table');
    if (isHtml) return { html: raw, references: [] };
    return renderAcademicMarkdown(raw);
  }, [data]);

  if (err) {
    return (
      <div className="main-container">
        <div className="alert alert-danger">{err}</div>
        <Link to="/assignments" className="btn btn-outline-primary">
          <i className="fas fa-arrow-left me-1"></i>Back to assignments
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="main-container">
        <div className="text-muted">Loading assignment…</div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="row">
        <div className="col-lg-10 mx-auto">
          <Link to="/assignments" className="text-muted small">
            <i className="fas fa-arrow-left me-1"></i>Back to assignments
          </Link>

          <div className="card mt-2 mb-4">
            <div
              style={{
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                color: '#fff',
                padding: '1.4rem',
                borderRadius: '12px 12px 0 0',
              }}
            >
              <div className="mb-2">
                <span
                  className="me-1"
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    borderRadius: 20,
                    padding: '2px 9px',
                    fontSize: '0.75rem',
                  }}
                >
                  {safeText(data.assignment_type)}
                </span>
                {data.academic_level && (
                  <span
                    className="me-1"
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                      borderRadius: 20,
                      padding: '2px 9px',
                      fontSize: '0.75rem',
                    }}
                  >
                    {safeText(data.academic_level).replace('_', ' ')}
                  </span>
                )}
                {data.referencing_style && (
                  <span
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                      borderRadius: 20,
                      padding: '2px 9px',
                      fontSize: '0.75rem',
                    }}
                  >
                    {safeText(data.referencing_style).toUpperCase()}
                  </span>
                )}
              </div>
              <h3 className="mb-0" style={{ fontWeight: 700 }}>
                {safeText(data.topic)}
              </h3>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <p className="mb-1">
                    <strong>Status:</strong>{' '}
                    <span className={`status-badge ${safeText(data.status, 'pending').toLowerCase()}`}>
                      {safeText(data.status, 'pending')}
                    </span>
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="mb-1">
                    <strong>Words:</strong>{' '}
                    {(data.word_count_actual ?? 0).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <DownloadButton
                  href={`/assignment/${id}/download/pdf`}
                  filename={`Assignment_${id}.pdf`}
                  icon="fa-file-pdf"
                  label="Download PDF"
                  className="btn btn-cta"
                />
                <DownloadButton
                  href={`/assignment/${id}/download/docx`}
                  filename={`Assignment_${id}.docx`}
                  icon="fa-file-word"
                  label="Download DOCX"
                  className="btn btn-outline-primary"
                />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <article
                className="dissertation-article"
                style={{ lineHeight: 1.8, fontSize: '1.02rem' }}
                dangerouslySetInnerHTML={{ __html: rendered.html }}
              />

              {/* Prefer the AI-validated (web-searched, APA-grade) list when
                  present. Fall back to the inline URL extractor for legacy
                  assignments that predate the validator. */}
              {Array.isArray(data.references_validated) && data.references_validated.length > 0 ? (
                <>
                  <hr className="my-4" />
                  <h3 style={{ fontWeight: 700 }}>References</h3>
                  {data.references_stats && (
                    <p className="text-muted small mb-3">
                      {data.references_stats.valid} verified,{' '}
                      {data.references_stats.corrected} corrected,{' '}
                      {data.references_stats.invalid} flagged — all entries
                      checked against live web sources by the model.
                    </p>
                  )}
                  <ol style={{ lineHeight: 1.8, paddingLeft: '1.4rem' }}>
                    {data.references_validated.map((ref, i) => (
                      <ApaReference key={i} ref={ref} />
                    ))}
                  </ol>
                </>
              ) : rendered.references.length > 0 ? (
                <>
                  <hr className="my-4" />
                  <h3 style={{ fontWeight: 700 }}>References</h3>
                  <ol style={{ lineHeight: 1.8, paddingLeft: '1.4rem' }}>
                    {rendered.references.map((ref, i) => (
                      <li key={i} style={{ marginBottom: '0.5rem', wordBreak: 'break-word' }}>
                        {ref.author && <>{ref.author} </>}
                        {ref.year && <>({ref.year}). </>}
                        {ref.title && <em>{ref.title}. </em>}
                        <a href={ref.url} target="_blank" rel="noreferrer">
                          {ref.url}
                        </a>
                      </li>
                    ))}
                  </ol>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Markdown processing utilities
// ---------------------------------------------------------------------------

